import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  createStyles,
  Box,
  Grid,
  Title,
  ActionIcon,
  Divider,
  Text,
  rem,
  Loader,
  RingProgress,
  Center,
} from "@mantine/core";
import uploadIcon from "../../../assets/img/uploadIcon.svg";
import fileLogo from "../../../assets/img/fileLogo.svg";
import deleteIcon from "../../../assets/img/deleteIcon.svg";
import fetchResourceIcon from "../../../assets/img/fetchingResourceLogo.svg";
import dbStoringIcon from "../../../assets/img/storingDBLogo.svg";
import { Patient } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";
import {
  athenaHandler,
  filterByValueString,
  filterIdentifier,
  includesAthenaHealth,
} from "../../../utils";
import { fileToBase64 } from "../../HomePage.utils";
import { showNotification } from "@mantine/notifications";
import { useSelector } from "react-redux";
import { AppState } from "../../../store/types/appState";
import { IconCheck } from "@tabler/icons-react";
import _ from "lodash";

const useStyles = createStyles(() => ({
  modal: {
    ".mantine-Modal-title": {
      fontSize: "22px",
      fontWeight: 600,
      lineHeight: "30px",
      textAlign: "center",
    },
    ".mantine-Modal-close": {
      width: "32px",
      height: "32px",
      color: "#262626",
    },
  },
  heading: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: "30px",
    textAlign: "center",
  },
  step: {
    width: "150px",
    height: "90px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  inputLabel: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "21px",
    textAlign: "left",
    ".mantine-TextInput-input": {
      height: "50px",
      padding: "13px 16px 13px 16px",
      borderRadius: "8px",
      border: "1px 0px 0px 0px",
    },
  },
  divider: {
    width: "80%",
  },
  uploadHeading: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "30px",
    textAlign: "left",
    marginBottom: "24px",
  },
  fileUploadText: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "22px",
    textAlign: "left",
    color: "#474E5B",
  },
  cancelButton: {
    width: "100px",
    height: "40px",
    padding: "9px 20px",
    borderRadius: "4px",
    border: "1px solid #1B75BC",
    marginRight: "24px",
    color: "#1C1C1C",
  },
  storeDocumentButton: {
    width: "200px",
    height: "40px",
    padding: "9px 20px",
    background: "#1B75BC",
  },
  uploadicon: {
    width: "24px",
    height: "24px",
  },
  icon: {
    position: "absolute",
    top: 10,
    left: 10,
  },
  fileIcon: {
    width: "50px",
    height: "50px",
    marginRight: "16px",
  },
  disableText: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0px",
    textAlign: "center",
    color: "#99A0AD",
    marginTop: "10px",
  },
  stepContainer: {
    height: "122px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #C9CDD3",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  uploadTextArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "40px",
    height: "140px",
    borderStyle: "dashed",
    borderWidth: "1px",
    borderColor: "#E5E5E5",
  },
  selectedFileArea: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E5E5E5",
    padding: "16px 20px",
    borderRadius: "8px",
    marginBottom: "50px",
  },
}));

interface MedicalDocumentModalProps {
  isMedicalDocumentModalOpen: boolean;
  setIsMedicalDocumentModalOpen: (val: boolean) => void;
  patientDetails: Patient;
  getPatientName: (name: any) => string;
  callbackValue: any;
}

const MedicalDocumentModal = ({
  isMedicalDocumentModalOpen,
  setIsMedicalDocumentModalOpen,
  patientDetails,
  getPatientName,
  callbackValue,
}: MedicalDocumentModalProps) => {
  const medplum = useMedplum();
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadDisabled, setIsUploadDisabled] = useState(true);
  const { classes } = useStyles();
  const logginedUser = useSelector((state: AppState) => state) as any;
  const [loadingStep, setLoadingStep] = useState(0);
  const [showStep, setSetshowStep] = useState(false);

  const handleDocumentNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDocumentName(event.target.value);
  };

  const checkUploadButtonState = () => {
    setIsUploadDisabled(!documentName || !selectedFile);
  };

  useEffect(() => {
    checkUploadButtonState();
  }, [documentName, selectedFile]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    setSelectedFile(file);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
  };

  const handleUploadAreaClick = () => {
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleUploadButtonClick = async () => {
    if (!selectedFile) return;

    const isAthenaPatient = includesAthenaHealth(patientDetails?.identifier);

    const filteredIdentifiers =
      patientDetails?.identifier &&
      filterIdentifier(patientDetails?.identifier, "athena_patient_id");

    const athenaPractionerIdentifier =
      patientDetails?.identifier &&
      filterIdentifier(patientDetails?.identifier, "athena_practitioner_id");

    if (!isAthenaPatient) {
      showNotification({
        color: "red",
        message: "Please select an Athena Health Patient!",
        autoClose: true,
      });
      return;
    }

    const filteredData = filterByValueString(
      (logginedUser?.app?.profile?.extension as any) || [],
      "athenahealth"
    );

    if (!filteredData?.length) {
      showNotification({
        color: "red",
        message: "Please connect with Athena Client first",
        autoClose: true,
      });
      return;
    }
    setSetshowStep(true);

    const base64 = await fileToBase64(selectedFile);

    try {
      const uploadDocPayload = {
        eventType: "uploadDoc",
        patientId: filteredIdentifiers?.[0]?.value,
        practitionerId: "1225652472001060",
        docName: documentName,
        docData: base64,
        accessToken:
          filteredData[filteredData?.length - 1]?.extension?.[0]?.valueString ||
          "",
        fileType:
          selectedFile?.type === "application/pdf"
            ? "text/xml"
            : selectedFile?.type,
      };
      const res = await athenaHandler(medplum, uploadDocPayload);

      if (res.statusCode === 201) {
        showNotification({
          color: "green",
          message: "Document Uploaded Successfully",
          autoClose: true,
        });
        callbackValue(true);
      } else {
        showNotification({
          color: "red",
          message: `Failed to upload document!`,
          autoClose: true,
        });
        callbackValue(false);
      }

      setIsMedicalDocumentModalOpen(false);
    } catch (error) {
      console.error("Error uploading document:", error);
      setIsMedicalDocumentModalOpen(false);
    }
  };

  const StepComponent: React.FC<any> = ({ disabled }) => {
    const steps = [
      { label: "Document", icon: fetchResourceIcon },
      { label: "Storing Doc in HealthConnect", icon: dbStoringIcon },
    ];

    useEffect(() => {
      let timer: any;
      if (showStep) {
        const timer = setTimeout(() => {
          setLoadingStep((prevStep) => prevStep + 1);
        }, 3000);
      }
      return () => clearTimeout(timer);
    }, [loadingStep, steps.length, showStep]);

    const Progress = () => {
      return (
        <RingProgress
          sections={[{ value: 100, color: "teal" }]}
          size={55}
          thickness={5}
          label={
            <Center>
              <ActionIcon color="teal" variant="light" radius="xl" size="md">
                <IconCheck style={{ width: rem(22), height: rem(22) }} />
              </ActionIcon>
            </Center>
          }
        />
      );
    };

    return (
      <Box className={classes.stepContainer}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <Box className={classes.step}>
              {index < loadingStep ? (
                <Progress />
              ) : index === loadingStep ? (
                <Box style={{ position: "relative" }}>
                  <Loader
                    size={"lg"}
                    color="green"
                    style={{ marginRight: "8px" }}
                  />
                  <ActionIcon
                    color="gray"
                    variant="light"
                    radius="xl"
                    size="sm"
                    className={classes.icon}
                  >
                    <img
                      src={
                        index === steps.length - 1
                          ? dbStoringIcon
                          : fetchResourceIcon
                      }
                      style={{ width: rem(22), height: rem(22) }}
                    />
                  </ActionIcon>
                </Box>
              ) : (
                <img
                  src={
                    index === steps.length - 1
                      ? dbStoringIcon
                      : fetchResourceIcon
                  }
                />
              )}
              <Text className={classes.disableText}>
                {index !== steps.length - 1 && index === loadingStep
                  ? `${step.label} Uploading to AthenaHealth`
                  : index < loadingStep
                  ? `${step.label} Uploaded `
                  : step.label}
              </Text>
            </Box>
            {index !== steps.length - 1 && (
              <Divider my="xs" className={classes.divider} />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Modal
      opened={isMedicalDocumentModalOpen}
      title="Upload medical document"
      onClose={() => setIsMedicalDocumentModalOpen(false)}
      size="lg"
      className={classes.modal}
    >
      <TextInput
        label="Document name"
        placeholder="Enter document name"
        value={documentName}
        onChange={handleDocumentNameChange}
        className={classes.inputLabel}
        sx={{
          marginBottom: "24px",
        }}
      />
      <Grid style={{ marginBottom: "16px" }}>
        <Grid.Col span={6}>
          <TextInput
            label="Patient name"
            value={getPatientName(patientDetails?.name)}
            disabled
            className={classes.inputLabel}
            style={{ marginRight: "24px" }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Doctor name"
            value={patientDetails?.meta?.author?.display}
            disabled
            className={classes.inputLabel}
          />
        </Grid.Col>
      </Grid>
      {selectedFile ? (
        <Box className={classes.selectedFileArea}>
          <img src={fileLogo} className={classes.fileIcon} alt="file Logo" />
          <Title className={classes.fileUploadText}>{selectedFile.name}</Title>
          <ActionIcon
            variant="link"
            color="red"
            onClick={handleDeleteFile}
            style={{ marginLeft: "auto" }}
          >
            <img src={deleteIcon} alt="delete icon" />
          </ActionIcon>
        </Box>
      ) : (
        <Box
          className={classes.uploadTextArea}
          onClick={handleUploadAreaClick}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <img
            src={uploadIcon}
            className={classes.uploadicon}
            alt="Upload Icon"
          />
          <Title className={classes.fileUploadText}>
            Drag and drop file or click to browse
          </Title>
          <input
            type="file"
            accept=".xml,.rtf,.pdf"
            onChange={handleFileInput}
            style={{ display: "none" }}
            id="fileInput"
          />
        </Box>
      )}
      <Box style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outline"
          onClick={() => setIsMedicalDocumentModalOpen(false)}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUploadButtonClick}
          className={classes.storeDocumentButton}
          disabled={isUploadDisabled}
          sx={{
            opacity: isUploadDisabled ? 0.5 : 1,
          }}
        >
          Store document in EHR
        </Button>
      </Box>
      {showStep && <StepComponent />}
    </Modal>
  );
};

export default MedicalDocumentModal;
